import * as Sentry from '@sentry/react';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { version } from '../../package.json';
import appConfig from '../config/app.config';
import { mixpanelEventHandler } from '../utilities/mixpanel';
import useAuth from './useAuth';

const useMixpanel = () => {
  const { userQuery } = useAuth();
  const location = useLocation();

  const mixpanelTrack = useCallback(
    (eventName: string, eventProperties: any = {}) => {
      if (!eventName) return;

      try {
        addSystemContextToEventProperties(eventProperties, version);
        addUserContextToEventProperties(eventProperties, userQuery);
        addNavigationContextToEventProperties(eventProperties, location);

        mixpanelEventHandler(eventName, eventProperties);
      } catch (error) {
        console.log('Mixpanel track error: ', error);
        if (appConfig.isLiveEnvironment()) Sentry.captureException(error);
      }
    },
    [location, userQuery],
  );

  return { mixpanelTrack };
};

function addSystemContextToEventProperties(eventProperties: any, version: string) {
  eventProperties.packageVersion = version;
}

function addUserContextToEventProperties(eventProperties: any, userQuery: any) {
  if (!eventProperties.userId && userQuery.data?.me?.id) {
    eventProperties.userId = userQuery.data?.me?.id;
    eventProperties.organizationAccountId = userQuery.data?.me?.organizationAccount?.id;
    eventProperties.organizationAccountName = userQuery.data?.me?.organizationAccount?.name;
    eventProperties.userRoleId = userQuery.data?.me?.userRole?.id;
    eventProperties.userRoleName = userQuery.data?.me?.userRole?.name;
  }
}

function addNavigationContextToEventProperties(eventProperties: any, location: any) {
  if (!location) return;
  eventProperties.pathname = location.pathname;
  eventProperties.search = location.search;
  eventProperties.hash = location.hash;
  eventProperties.state = location.state;
  eventProperties.key = location.key;
}

export default useMixpanel;
